import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { MESSAGES } from './constants';

/*
DATA EXAMPLES (do not delete):

WebSocket message: Portfolio Rebalancing Summary {
  "message": "Portfolio Rebalancing Summary",
  "Decision date": "2020-01-23",
  "regime": "BULL",
  "target_positions": 47,
  "actions": {
    "sold": 2,
    "bought": 3,
    "held": 44,
    "total": 47
  },
  "trades": {
    "sold": [
      {
        "ticker": "BKR",
        "alpha": "0.0%"
      },
      {
        "ticker": "FANG",
        "alpha": "0.0%"
      }
    ],
    "bought": [
      {
        "ticker": "BKR",
        "alpha": "80.5%"
      },
      {
        "ticker": "BIIB",
        "alpha": "51.3%"
      },
      {
        "ticker": "MCHP",
        "alpha": "50.6%"
      }
    ],
    "held": [
      {
        "ticker": "FTNT",
        "alpha": "0.0%"
      },
      {
        "ticker": "SNPS",
        "alpha": "0.0%"
      },
      {
        "ticker": "MDLZ",
        "alpha": "0.0%"
      },
      {
        "ticker": "CHTR",
        "alpha": "0.0%"
      },
      {
        "ticker": "TTWO",
        "alpha": "0.0%"
      },
      {
        "ticker": "TSLA",
        "alpha": "0.0%"
      },
      {
        "ticker": "CDW",
        "alpha": "0.0%"
      },
      {
        "ticker": "META",
        "alpha": "0.0%"
      },
      {
        "ticker": "CCEP",
        "alpha": "0.0%"
      },
      {
        "ticker": "DXCM",
        "alpha": "0.0%"
      },
      {
        "ticker": "MDB",
        "alpha": "0.0%"
      },
      {
        "ticker": "KHC",
        "alpha": "0.0%"
      },
      {
        "ticker": "MAR",
        "alpha": "0.0%"
      },
      {
        "ticker": "KDP",
        "alpha": "0.0%"
      },
      {
        "ticker": "KLAC",
        "alpha": "0.0%"
      },
      {
        "ticker": "DDOG",
        "alpha": "0.0%"
      },
      {
        "ticker": "TTD",
        "alpha": "0.0%"
      },
      {
        "ticker": "PANW",
        "alpha": "0.0%"
      },
      {
        "ticker": "AVGO",
        "alpha": "0.0%"
      },
      {
        "ticker": "NVDA",
        "alpha": "0.0%"
      },
      {
        "ticker": "MRVL",
        "alpha": "0.0%"
      },
      {
        "ticker": "AMD",
        "alpha": "0.0%"
      },
      {
        "ticker": "VRTX",
        "alpha": "0.0%"
      },
      {
        "ticker": "MSTR",
        "alpha": "0.0%"
      },
      {
        "ticker": "ADP",
        "alpha": "0.0%"
      },
      {
        "ticker": "WDAY",
        "alpha": "0.0%"
      },
      {
        "ticker": "MNST",
        "alpha": "0.0%"
      },
      {
        "ticker": "WBD",
        "alpha": "0.0%"
      },
      {
        "ticker": "CRWD",
        "alpha": "0.0%"
      },
      {
        "ticker": "PDD",
        "alpha": "0.0%"
      },
      {
        "ticker": "MU",
        "alpha": "0.0%"
      },
      {
        "ticker": "NFLX",
        "alpha": "0.0%"
      },
      {
        "ticker": "VRSK",
        "alpha": "0.0%"
      },
      {
        "ticker": "AZN",
        "alpha": "0.0%"
      },
      {
        "ticker": "REGN",
        "alpha": "0.0%"
      },
      {
        "ticker": "ON",
        "alpha": "0.0%"
      },
      {
        "ticker": "INTU",
        "alpha": "0.0%"
      },
      {
        "ticker": "LIN",
        "alpha": "0.0%"
      },
      {
        "ticker": "CDNS",
        "alpha": "0.0%"
      },
      {
        "ticker": "NXPI",
        "alpha": "0.0%"
      },
      {
        "ticker": "TEAM",
        "alpha": "0.0%"
      },
      {
        "ticker": "PYPL",
        "alpha": "0.0%"
      },
      {
        "ticker": "ZS",
        "alpha": "0.0%"
      }
    ]
  },
  "performance": {
    "portfolio_value": "104746.33",
    "portfolio_return_pct": "4.75",
    "portfolio_annualized_return_pct": "79.74",
    "benchmark_value": 9216.98,
    "benchmark_return_pct": 5.54,
    "alpha_cumulative_pct": -0.79,
    "alpha_annualized_pct": -13.35,
    "sharpe_ratio": 8.29,
    "volatility_annualized_pct": 9.14,
    "days_elapsed": 15,
    "cash_pct": "1.4",
    "num_positions": 47
  }
}
*/

// Performance metrics from Portfolio Rebalancing Summary
interface PerformanceMetrics {
  portfolio_value: string | number;
  portfolio_return_pct: string | number;
  portfolio_annualized_return_pct: string | number;
  benchmark_value: number;
  benchmark_return_pct: number;
  alpha_cumulative_pct: number;
  alpha_annualized_pct: number;
  sharpe_ratio: number;
  volatility_annualized_pct: number;
  days_elapsed: number;
  cash_pct: string | number;
  num_positions: number;
}

// Portfolio Rebalancing Summary data structure
interface PortfolioRebalancingData {
  message: string;
  'Decision date': string; // e.g., "2020-01-23"
  regime: string;
  target_positions: number;
  actions: {
    sold: number;
    bought: number;
    held: number;
    total: number;
  };
  trades: {
    sold: Array<{ ticker: string; alpha: string }>;
    bought: Array<{ ticker: string; alpha: string }>;
    held: Array<{ ticker: string; alpha: string }>;
  };
  performance?: PerformanceMetrics;
}

interface EventData {
  message: string;
  data?: PortfolioRebalancingData;
  timestamp?: string;
}

interface PortfolioPerformanceChartProps {
  data?: EventData[];
}

interface ChartDataPoint {
  decisionDate: string;
  alphaCumulative: string;
  portfolioReturn: string;
  benchmarkReturn: string;
}

/**
 * PortfolioPerformanceChart Component
 *
 * Displays a line chart showing portfolio return performance
 * from PORTFOLIO_REBALANCING_SUMMARY events with performance data.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of relevant events from WebSocket
 */
const PortfolioPerformanceChart: React.FC<PortfolioPerformanceChartProps> = ({
  data = [],
}) => {
  const chartData = useMemo(() => {
    // Filter and process only PORTFOLIO_REBALANCING_SUMMARY events with performance data
    const performanceEvents = data.filter(
      (event) =>
        event.message === MESSAGES.PORTFOLIO_REBALANCING_SUMMARY &&
        event.data?.performance
    );

    return performanceEvents
      .map((event) => {
        if (!event.data || !event.data.performance) return null;

        const perf = event.data.performance;

        // Extract alpha cumulative (already in percentage from alpha_cumulative_pct)
        const alphaCumulativePct =
          parseFloat(String(perf.alpha_cumulative_pct)) || 0;

        // Extract portfolio return (already in percentage from portfolio_return_pct)
        const portfolioReturnPct =
          parseFloat(String(perf.portfolio_return_pct)) || 0;

        // Extract benchmark return (already in percentage from benchmark_return_pct)
        const benchmarkReturnPct =
          parseFloat(String(perf.benchmark_return_pct)) || 0;

        // Extract Decision date for X axis
        if (!event.data['Decision date']) return null;

        const date = new Date(event.data['Decision date']);
        // Check if date is valid
        if (isNaN(date.getTime())) return null;

        // Format as dd-mm-yyyy
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const decisionDate = `${day}-${month}-${year}`;

        return {
          decisionDate: decisionDate,
          alphaCumulative: alphaCumulativePct.toFixed(2),
          portfolioReturn: portfolioReturnPct.toFixed(2),
          benchmarkReturn: benchmarkReturnPct.toFixed(2),
        };
      })
      .filter((item): item is ChartDataPoint => item !== null);
  }, [data]);

  // Calculate Y-axis domain with 5% padding, ensuring 0 is always included
  const yAxisDomain = useMemo((): [number, number] => {
    if (chartData.length === 0) return [0, 100];

    const allValues = chartData.flatMap((d) => [
      parseFloat(d.alphaCumulative),
      parseFloat(d.portfolioReturn),
      parseFloat(d.benchmarkReturn),
    ]);
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    const range = maxValue - minValue;
    // If range is 0 or very small, use absolute padding, otherwise use 5% of range
    const padding =
      range === 0 ? Math.max(Math.abs(maxValue) * 0.05, 5) : range * 0.05;

    const min = Number((minValue - padding).toFixed(2));
    const max = Number((maxValue + padding).toFixed(2));
    return [min, max];
  }, [chartData]);

  if (!data || data.length === 0) {
    return (
      <div className="p-5 text-center text-foreground">
        <p>No portfolio performance data available</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div>
        <h3 className="mb-5 font-display text-foreground">
          Portfolio Performance
        </h3>
        <div
          className="animate-pulse rounded-md border border-border bg-muted flex h-[300px] items-end px-4 pb-4 pt-6"
          aria-hidden
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 600 260"
            preserveAspectRatio="none"
            aria-hidden
          >
            <polyline
              points="0,200 80,170 160,155 220,130 280,145 340,110 400,85 460,100 520,70 600,50"
              fill="none"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="0,220 80,210 160,195 220,185 280,190 340,175 400,160 460,165 520,145 600,130"
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="mt-2.5 text-center text-xs text-muted-foreground">
          Populates as rebalancing events arrive
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-5 font-display text-foreground">
        Portfolio Performance
      </h3>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="decisionDate"
            angle={-45}
            textAnchor="end"
            height={100}
            interval={30}
          />
          <YAxis domain={yAxisDomain} />
          <Tooltip
            itemSorter={(item) =>
              item.dataKey === 'portfolioReturn'
                ? 0
                : item.dataKey === 'benchmarkReturn'
                  ? 1
                  : 2
            }
            itemStyle={{
              padding: '0px 6px 12px 6px',
            }}
            contentStyle={{
              margin: 0,
              padding: '0px 6px 6px 6px',
            }}
          />
          <Legend />
          <ReferenceLine y={0} stroke="#666" strokeDasharray="10 2" />
          <Line
            type="monotone"
            dataKey="portfolioReturn"
            stroke="#8884d8"
            strokeWidth={2}
            dot={false}
            name="Portfolio Return"
            unit="%"
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="benchmarkReturn"
            stroke="#82ca9d"
            strokeWidth={2}
            dot={false}
            name="Index Performance"
            unit="%"
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortfolioPerformanceChart;
